import cx from 'clsx';
import { Button } from '@vechaiui/react';
import { Dialog as DialogPrimitive, Transition } from '@headlessui/react';
import { Fragment, useEffect, useState } from 'react';
import { useLocalization } from '@fluent/react';

const Dialog: React.FC<DialogProps> = ({
  children,
  delayConfirmButton,
  isModal,
  isOpen,
  closeButton,
  confirmButton,
  onClose,
  onConfirm,
  title,
}) => {
  const { l10n } = useLocalization();

  const [disableConfirmButton, setDisableConfirmButton] = useState(true);

  useEffect(() => {
    delayConfirmButton &&
      setTimeout(() => {
        setDisableConfirmButton(false);
      }, 1500);
  }, [delayConfirmButton]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <DialogPrimitive as="div" className="relative z-10" onClose={() => onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-full p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPrimitive.Panel
                className={cx(
                  'overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl',
                  isModal ? 'max-w-4xl w-full' : 'max-w-md w-full',
                )}
              >
                <div className="p-4 space-y-4 2xl:p-6">
                  {!!title && (
                    <DialogPrimitive.Title as="h2" className="text-xl">
                      {title}
                    </DialogPrimitive.Title>
                  )}

                  {children}
                </div>

                {(onClose || onConfirm) && (
                  <footer className="flex justify-end gap-2 px-4 py-2 bg-neutral-100">
                    {onClose && (
                      <Button color="red" variant="ghost" type="button" onClick={onClose}>
                        {closeButton ?? l10n.getString('cancel')}
                      </Button>
                    )}
                    {onConfirm && (
                      <Button
                        color="primary"
                        disabled={
                          !!delayConfirmButton && delayConfirmButton && disableConfirmButton
                        }
                        type="submit"
                        variant="solid"
                        onClick={onConfirm}
                      >
                        {confirmButton ?? l10n.getString('confirm')}
                      </Button>
                    )}
                  </footer>
                )}
              </DialogPrimitive.Panel>
            </Transition.Child>
          </div>
        </div>
      </DialogPrimitive>
    </Transition>
  );
};
export type DialogProps = {
  delayConfirmButton?: boolean;
  isModal?: boolean;
  isOpen: boolean;
  closeButton?: React.ReactNode;
  confirmButton?: React.ReactNode;
  onClose?: () => void;
  onConfirm?: () => void;
  title?: string;
};

export default Dialog;
